import './App.css';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ALL_TOOLS, MOST_USED_TOOLS, TOOLS_BY_CATEGORY, TOTAL_TOOL_COUNT, extractSteamId } from './toolData';
import Sidebar from './Sidebar';
import analytics from './analytics';
import InventoryCard, { useInventory, Medals } from './Inventory';
import CsrepCard, { CsrepPlaceholder, useCsrep } from './CSRep';

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
};

function FaviconImage({ url, size = 48, priority = false }) {
  const [error, setError] = useState(false);
  const domain = hostOf(url);
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

  if (error) {
    return <div className="icon">🔗</div>;
  }

  return (
    <img
      src={faviconUrl}
      alt=""
      className="icon favicon"
      width={size}
      height={size}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      onError={() => setError(true)}
    />
  );
}

const DEFAULT_PINS = ['cs2-stats', 'steam-calculator', 'skinpock'];
// 4 because the pins are bound to keys 1-4.
const MAX_PINS = 4;
const MOST_USED_TOOL_LIMIT = 10;
const PINS_KEY = 'pp.pins.v1';
// Pre-v1 favorites (max 9). Read once to migrate, never written again.
const LEGACY_PINS_KEY = 'player-properties-favorites';

const isPinList = (v) =>
  Array.isArray(v) && v.length <= MAX_PINS && v.every((id) => ALL_TOOLS[id]);

function loadPins() {
  try {
    const stored = localStorage.getItem(PINS_KEY);
    if (stored !== null) {
      const pins = JSON.parse(stored);
      return isPinList(pins) ? pins : DEFAULT_PINS;
    }
    const legacy = JSON.parse(localStorage.getItem(LEGACY_PINS_KEY));
    if (Array.isArray(legacy)) {
      const migrated = legacy.filter((id) => ALL_TOOLS[id]).slice(0, MAX_PINS);
      if (migrated.length) return migrated;
    }
  } catch {
    // Storage blocked or corrupt — defaults below.
  }
  return DEFAULT_PINS;
}

// Single-tool open from a keypress. The keydown is the user activation, so this
// isn't popup-blocked; window.open also skips the global outbound click tracker,
// which would otherwise double-count it next to trackToolClick.
const openTool = (url) => window.open(url, '_blank', 'noopener,noreferrer');

function ToolTile({ tool, steamId, size }) {
  return (
    <span className={`tool-tile tool-tile-${size}`}>
      <FaviconImage url={tool.urlTemplate(steamId)} size={Math.round(size * 0.6)} priority={size === 48} />
    </span>
  );
}

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 17v5" />
      <path d="M9 3h6l-1 6 4 4H6l4-4-1-6z" />
    </svg>
  );
}

// --- Leetify display formatting ---------------------------------------------
// Metrics are shown EXACTLY as Leetify presents them (their guidelines): the
// skill ratings are 0-100 integers, the Leetify/Clutch ratings are signed with
// two decimals, win rate / HS are integer percents, TTD ("Time to Damage") is
// milliseconds. We round/sign for display but never rescale or rename a metric.
const formatLeetifyRating = (n) => (n >= 0 ? `+${n.toFixed(2)}` : n.toFixed(2));
const formatLeetifyScore = (n) => Math.round(n).toString(); // 0-100
const formatWinrate = (n) => `${Math.round(n * 100)}%`; // 0.6429 -> 64%
const formatPercent = (n) => `${Math.round(n)}%`; // 18.02 -> 18%
const formatCount = (n) => Math.round(n).toLocaleString('en-US'); // 2361 -> 2,361
const formatMs = (n) => `${Math.round(n)}ms`; // 587 -> 587ms
const formatDegrees = (n) => `${n.toFixed(1)}°`; // 5.9318 -> 5.9°

// CS2 Premier rating tier colors — one tier per 5,000 points, 30,000+ is gold.
// A boundary value (exactly 5,000) belongs to the higher tier via Math.floor.
const PREMIER_TIER_COLORS = [
  '#B0B0B0', // 0 - 4,999 grey
  '#6FB7E8', // 5,000 - 9,999 light blue
  '#4A7FE0', // 10,000 - 14,999 blue
  '#9B4DCA', // 15,000 - 19,999 purple
  '#F472B6', // 20,000 - 24,999 pink
  '#E5453C', // 25,000 - 29,999 red
];
function premierTierColor(rating) {
  if (rating == null) return null;
  if (rating >= 30000) return '#F0C040'; // gold
  return PREMIER_TIER_COLORS[Math.max(0, Math.floor(rating / 5000))];
}

// CS competitive skill groups, 1-indexed to match the game's IDs — the API uses
// 0 for unranked (see the competitive ranks array), so 1 = Silver I … 18 =
// Global Elite.
const WINGMAN_RANKS = [
  ['S1', 'Silver I'],
  ['S2', 'Silver II'],
  ['S3', 'Silver III'],
  ['S4', 'Silver IV'],
  ['SE', 'Silver Elite'],
  ['SEM', 'Silver Elite Master'],
  ['GN1', 'Gold Nova I'],
  ['GN2', 'Gold Nova II'],
  ['GN3', 'Gold Nova III'],
  ['GNM', 'Gold Nova Master'],
  ['MG1', 'Master Guardian I'],
  ['MG2', 'Master Guardian II'],
  ['MGE', 'Master Guardian Elite'],
  ['DMG', 'Distinguished Master Guardian'],
  ['LE', 'Legendary Eagle'],
  ['LEM', 'Legendary Eagle Master'],
  ['SMFC', 'Supreme Master First Class'],
  ['GE', 'Global Elite'],
];

// id → { abbr, full }, or null when unranked (0/null) or out of range. Callers
// fall back to the raw number for out-of-range ids rather than mislabeling.
function wingmanRank(id) {
  if (id == null || id < 1) return null;
  const entry = WINGMAN_RANKS[Math.round(id) - 1];
  return entry ? { abbr: entry[0], full: entry[1] } : null;
}

// Relative age coarsened to its largest single unit ("2y ago", "11mo ago",
// "3d ago") — no false precision like "3y 4mo ago". Shared by the ban flag and
// the // activity "Last match" entry.
function formatRelativeAge(iso) {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const days = Math.floor((Date.now() - then) / 86400000);
  const years = Math.floor(days / 365);
  if (years >= 1) return `${years}y ago`;
  const months = Math.floor(days / 30);
  if (months >= 1) return `${months}mo ago`;
  if (days >= 1) return `${days}d ago`;
  return 'today';
}

// Absolute "Jan 2020" anchor. Used for Leetify's first TRACKED match — not when
// the player started CS, hence the honest "First tracked" label at the call site.
function formatMonthYear(iso) {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  return new Date(then).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Exact date ("Jan 4, 2020") / date+time ("Jun 8, 2026, 8:02 PM") for hover
// tooltips on the coarsened // activity values.
function formatFullDate(iso) {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  return new Date(then).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatFullDateTime(iso) {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  return new Date(then).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Leetify-reported platform ban, shown inside the ban alert. Shows the most
// recent ban when there are several.
function leetifyBanText(bans) {
  if (!bans || bans.length === 0) return null;
  const ban = [...bans].sort(
    (a, b) => Date.parse(b.bannedSince) - Date.parse(a.bannedSince)
  )[0];
  const age = formatRelativeAge(ban.bannedSince);
  return `${ban.platform.toUpperCase()} ban${age ? ` ${age}` : ''}`;
}

// Line-shaped skeleton placeholder. The non-breaking space makes it occupy the
// exact line height of the text it stands in for, so nothing shifts when real
// content swaps in. Width in `ch` matches monospace character counts.
function SkeletonLine({ width, className = '' }) {
  return (
    <span
      className={`skeleton-block skeleton-line ${className}`}
      style={width ? { width } : undefined}
    >
      &nbsp;
    </span>
  );
}

function WarningIcon() {
  return (
    <svg className="pp-alert-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 2 21h20L12 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 10v5M12 18v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Mirrors the real sidebar (avatar, name, five key/value rows) so the swap to
// real data is shift-free.
function ProfileSkeleton() {
  return (
    <aside className="pp-card pp-profile" aria-hidden="true">
      <div className="skeleton-block pp-skeleton-avatar"></div>
      <div className="pp-name-block">
        <SkeletonLine width="6ch" className="pp-label" />
        <SkeletonLine width="10ch" className="pp-name" />
      </div>
      <div className="pp-kv">
        {[0, 1, 2, 3, 4].map((i) => (
          <div className="pp-kv-row" key={i}><SkeletonLine width="100%" /></div>
        ))}
      </div>
    </aside>
  );
}

// Every GetPlayerBans field that means something, as key = value lines.
// EconomyBan is "none" | "probation" | "banned".
function steamBanLines(bans) {
  if (!bans) return [];
  const anyBan = bans.VACBanned || bans.NumberOfGameBans > 0;
  return [
    bans.VACBanned && ['vacBans', bans.NumberOfVACBans || 1],
    bans.NumberOfGameBans > 0 && ['gameBans', bans.NumberOfGameBans],
    anyBan && bans.DaysSinceLastBan != null && [
      'lastBan', formatRelativeAge(new Date(Date.now() - bans.DaysSinceLastBan * 86400000).toISOString()),
    ],
    bans.EconomyBan && bans.EconomyBan !== 'none' && ['economyBan', `"${bans.EconomyBan}"`],
    bans.CommunityBanned && ['communityBanned', 'true'],
  ].filter(Boolean);
}

function ProfileCard({ steamId, player, bans, steamLevel, leetify, inventory, accountAge, visibility }) {
  const leetifyBan = leetify.status === 'ready' ? leetifyBanText(leetify.data.bans) : null;
  const banLines = steamBanLines(bans);
  const data = leetify.status === 'ready' ? leetify.data : {};

  // Mixed formats are intentional: absolute anchor for the tracking start,
  // relative for recency. "firstTracked" — NOT "playing since" — because
  // first_match_date is only Leetify's first tracked match.
  const rows = [
    { key: 'accountAge', value: accountAge },
    { key: 'steamLevel', value: steamLevel },
    { key: 'visibility', value: `"${visibility}"` },
    {
      key: 'firstTracked',
      value: formatMonthYear(data.firstMatchDate),
      title: formatFullDate(data.firstMatchDate)
        ? `First match tracked by Leetify — ${formatFullDate(data.firstMatchDate)}`
        : undefined,
    },
    {
      key: 'lastMatch',
      value: formatRelativeAge(data.lastMatchFinishedAt),
      title: formatFullDateTime(data.lastMatchFinishedAt) ?? undefined,
    },
    { key: 'matches', value: data.totalMatches != null ? formatCount(data.totalMatches) : null },
    inventory.status === 'private' && { key: 'inventory', value: '"private"' },
    inventory.status === 'unavailable' && { key: 'inventory', value: '"unavailable"' },
  ].filter((r) => r && r.value != null);

  return (
    <aside className="pp-card pp-profile">
      <a
        href={`https://steamcommunity.com/profiles/${steamId}`}
        target="_blank"
        rel="noopener noreferrer"
        data-track="steam-profile"
        className="pp-avatar-link"
      >
        <img
          src={player.avatarfull}
          alt="Steam avatar"
          className="pp-avatar"
          width="96"
          height="96"
          loading="eager"
          fetchPriority="high"
        />
      </a>
      <div className="pp-name-block">
        <div className="pp-label">name =</div>
        <div className="pp-name">
          <span className="pp-quote">"</span>{player.personaname}<span className="pp-quote">"</span>
        </div>
      </div>
      {(banLines.length > 0 || leetifyBan) && (
        <div className="pp-alert" role="note">
          <WarningIcon />
          <div>
            {banLines.map(([k, v]) => <div key={k}>{k} = <strong>{v}</strong></div>)}
            {leetifyBan && <div>{leetifyBan}</div>}
          </div>
        </div>
      )}
      <dl className="pp-kv">
        {rows.map((r) => (
          <div className="pp-kv-row" key={r.key}>
            <dt>{r.key}</dt>
            <dd title={r.title}>{r.value}</dd>
          </div>
        ))}
      </dl>
      {inventory.data && inventory.data.items && <Medals items={inventory.data.items} />}
    </aside>
  );
}

function StatCard({ label, value, color, title }) {
  return (
    <div className="pp-card pp-stat">
      <div className="pp-label">{label}</div>
      <div className="pp-stat-value" style={color ? { color } : undefined} title={title}>
        {value}
      </div>
    </div>
  );
}

function LeetifyStatCards({ data }) {
  // Wingman stays uncolored — its 1-18 rank scale is not the Premier rating
  // scale, so the Premier tier colors don't apply.
  const cards = [
    data.leetifyRating != null && {
      label: 'leetifyRating', value: formatLeetifyRating(data.leetifyRating), color: '#4fd1a5',
    },
    // Premier always renders: "-1" (uncolored) when the player has no rating.
    {
      label: 'premier',
      value: data.premier != null ? formatCount(data.premier) : '-1',
      color: premierTierColor(data.premier),
    },
    data.faceitLevel != null && {
      label: 'faceitLevel', value: formatLeetifyScore(data.faceitLevel), color: '#fb923c',
    },
    // Skill-group abbreviation with the full name on hover. 0/null = unranked →
    // no card; out-of-range id → raw number fallback, no tooltip.
    data.wingman != null && data.wingman >= 1 && {
      label: 'wingman',
      value: wingmanRank(data.wingman)?.abbr ?? formatLeetifyScore(data.wingman),
      title: wingmanRank(data.wingman)?.full,
    },
  ].filter(Boolean);

  return (
    <div className="pp-stats">
      {cards.map((c) => <StatCard key={c.label} {...c} />)}
    </div>
  );
}

function LeetifyCard({ data }) {
  // 0-100 skill bars; null fields are skipped, never rendered empty.
  const skills = [
    data.aim != null && { label: 'aim', value: data.aim },
    data.utility != null && { label: 'utility', value: data.utility },
    data.positioning != null && { label: 'positioning', value: data.positioning },
  ].filter(Boolean);

  const record = [
    data.winrate != null && { label: 'winRate', value: formatWinrate(data.winrate) },
    // Clutch is a signed, zero-centered rating with no 0-100 ceiling, so it's a
    // number, not a skill bar. The API returns a fraction (0.1452) but Leetify
    // presents it ×100 signed ("+14.52"), same convention as their CT/T ratings.
    // Opening uses the same scale.
    data.clutch != null && {
      label: 'clutch',
      value: formatLeetifyRating(data.clutch * 100),
      color: data.clutch > 0 ? '#4fd1a5' : undefined,
    },
    data.opening != null && {
      label: 'opening',
      value: formatLeetifyRating(data.opening * 100),
      color: data.opening > 0 ? '#4fd1a5' : undefined,
    },
  ].filter(Boolean);

  // Labels are Leetify's own metric names: preaim is their "Crosshair
  // Placement", in degrees.
  const aim = [
    data.reactionTimeMs != null && { label: 'timeToDamage', value: formatMs(data.reactionTimeMs) },
    data.preaim != null && { label: 'crosshairPlacement', value: formatDegrees(data.preaim) },
    data.accuracySpotted != null && { label: 'accuracySpotted', value: formatPercent(data.accuracySpotted) },
    data.accuracyHead != null && { label: 'headshotPct', value: formatPercent(data.accuracyHead) },
    data.sprayAccuracy != null && { label: 'sprayAccuracy', value: formatPercent(data.sprayAccuracy) },
    data.counterStrafing != null && { label: 'counterStrafing', value: formatPercent(data.counterStrafing) },
  ].filter(Boolean);

  return (
    <section className="pp-card pp-leetify">
      <div className="pp-leetify-grid">
        <div>
          <div className="pp-comment">{'// skills'}</div>
          {skills.map((s) => (
            <div className="pp-skill" key={s.label}>
              <div className="pp-skill-head">
                <span>{s.label}</span>
                <span className="pp-skill-value">{formatLeetifyScore(s.value)}</span>
              </div>
              <div className="pp-bar">
                <span className="pp-bar-fill" style={{ width: `${Math.max(0, Math.min(100, s.value))}%` }}></span>
              </div>
            </div>
          ))}
        </div>
        {[['// record', record], ['// aim', aim]].map(([heading, tiles]) => tiles.length > 0 && (
          <div key={heading}>
            <div className="pp-comment">{heading}</div>
            <div className="pp-tiles">
              {tiles.map((r) => (
                <div className="pp-tile" key={r.label}>
                  <div className="pp-label">{r.label}</div>
                  <div className="pp-tile-value" style={r.color ? { color: r.color } : undefined}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="pp-leetify-footer">
        {/* Leetify guideline 3.1: must read "View on Leetify" and be identifiable
            as a link via bold, underline, OR pink — we use bold. */}
        <a
          className="pp-link pp-leetify-link"
          href={data.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-track="leetify-profile"
        >
          [view on leetify →]
        </a>
        {/* Official "Data Provided by Leetify" badge — white/pink variant for our
            dark background, unmodified, linking to leetify.com (per guidelines). */}
        <a
          className="pp-leetify-badge"
          href="https://leetify.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Data Provided by Leetify"
        >
          <img src="/leetify/leetify-badge-white.png" alt="Data Provided by Leetify" />
        </a>
      </div>
    </section>
  );
}

// Shown instead of the stats when the proxy says no. Without it the section just
// vanishes and the page looks broken. `reason` comes from the proxy; upstream_404
// means Leetify has never tracked this account.
function LeetifyPlaceholder({ steamId, reason }) {
  const message =
    reason === 'upstream_404' ? 'no leetify data for this player'
    : reason === 'private' ? 'leetify profile is private'
    : 'leetify stats unavailable right now';
  return (
    <section className="pp-card pp-leetify-empty">
      <span className="pp-comment">{`// ${message}`}</span>
      <a
        className="pp-link pp-leetify-link"
        href={`https://leetify.com/app/profile/${steamId}`}
        target="_blank"
        rel="noopener noreferrer"
        data-track="leetify-profile"
      >
        [view on leetify →]
      </a>
    </section>
  );
}

// Same boxes as the real stat row and skills/record card.
function LeetifySkeleton() {
  return (
    <>
      <div className="pp-stats" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div className="pp-card pp-stat" key={i}>
            <SkeletonLine width="10ch" className="pp-label" />
            <SkeletonLine width="6ch" className="pp-stat-value" />
          </div>
        ))}
      </div>
      <div className="pp-card skeleton-block pp-skeleton-leetify" aria-hidden="true"></div>
    </>
  );
}

function App() {
  const { steamid: urlSteamId } = useParams();
  const navigate = useNavigate();
  const [steamId, setSteamId] = useState(urlSteamId || '76561198212301687');
  const [inputValue, setInputValue] = useState(urlSteamId || '76561198212301687');
  const [playerData, setPlayerData] = useState(null);
  const [banData, setBanData] = useState(null);
  const [apiError, setApiError] = useState(false);
  // Steam section skeleton state — true until the Steam calls resolve (ok or not).
  const [steamLoading, setSteamLoading] = useState(true);
  // Leetify stats load independently of the Steam data so they never block first
  // paint. status: 'loading' | 'ready' | 'unavailable'. Starts as 'loading' so
  // the whole header renders skeletons immediately, never a blank gap.
  const [leetify, setLeetify] = useState({ status: 'loading', data: null });
  const inventory = useInventory(steamId);
  const csrep = useCsrep(steamId);
  const [steamLevel, setSteamLevel] = useState(null);
  const [favorites, setFavorites] = useState(loadPins);
  // { surface, at } of the last refused pin; `at` re-arms the timer on repeats.
  const [pinsFullHint, setPinsFullHint] = useState(null);
  const [activeSection, setActiveSection] = useState('quick-access');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(favorites));
    } catch {
      // Storage blocked: pins live for this page view only.
    }
  }, [favorites]);

  useEffect(() => {
    if (!pinsFullHint) return;
    const t = setTimeout(() => setPinsFullHint(null), 2500);
    return () => clearTimeout(t);
  }, [pinsFullHint]);

  useEffect(() => {
    const ids = ['quick-access', 'most-used-tools', ...TOOLS_BY_CATEGORY.map(c => c.slug)];
    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          ratios.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0);
        });
        let best = null;
        let bestRatio = 0;
        ratios.forEach((r, id) => {
          if (r > bestRatio) {
            bestRatio = r;
            best = id;
          }
        });
        if (best) setActiveSection(best);
      },
      {
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    ids
      .map(id => document.getElementById(id))
      .filter(Boolean)
      .forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const sidebarSections = useMemo(() => {
    const entries = [
      { id: 'quick-access', label: 'quick-access', lineNumber: 1 },
      { id: 'most-used-tools', label: 'most-used-tools', lineNumber: 2 },
      { isBreaker: true, lineNumber: 3 },
    ];
    TOOLS_BY_CATEGORY.forEach((cat, i) => {
      entries.push({
        id: cat.slug,
        label: cat.slug,
        lineNumber: 4 + i,
      });
    });
    return entries;
  }, []);

  const mostUsedTools = useMemo(() => {
    const ranked = MOST_USED_TOOLS.slice(0, MOST_USED_TOOL_LIMIT).filter(({ id }) => ALL_TOOLS[id]);
    const topShare = Math.max(0, ...ranked.map(({ share }) => share || 0));
    return ranked.map(({ id, share }, i) => ({
      tool: ALL_TOOLS[id],
      rank: i + 1,
      share,
      // Bars are relative to #1 so the leader is always full width.
      bar: topShare && share ? (share / topShare) * 100 : 0,
    }));
  }, []);

  const scrollToSection = (id, source = 'sidebar') => {
    analytics.trackSidebar('nav_click', { item: id, source });
    setActiveSection(id);
    setSidebarOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  // Returns false when pinning is refused because quick-access is full.
  const togglePin = (toolId, analyticsContext = {}) => {
    const tool = ALL_TOOLS[toolId] || { id: toolId, name: toolId };

    if (favorites.includes(toolId)) {
      setFavorites(favorites.filter(id => id !== toolId));
      analytics.trackFavoriteRemoved(tool, analyticsContext);
      return true;
    }
    if (favorites.length >= MAX_PINS) return false;
    setFavorites([...favorites, toolId]);
    analytics.trackFavoriteAdded(tool, analyticsContext);
    return true;
  };

  // Pin buttons in most-used and the directory; a refused pin shows the hint
  // on the surface that was clicked.
  const pinFrom = (surface, tool) => {
    if (!togglePin(tool.id, { surface, category: tool.category })) {
      setPinsFullHint({ surface, at: Date.now() });
    }
  };
  const fullHintFor = (surface) =>
    pinsFullHint?.surface === surface ? 'quick-access is full, unpin one first' : '';

  const formatAccountAge = (timecreated) => {
    if (!timecreated) return 'Unknown';

    const createdDate = new Date(timecreated * 1000);
    const now = new Date();

    let years = now.getFullYear() - createdDate.getFullYear();
    let months = now.getMonth() - createdDate.getMonth();
    let days = now.getDate() - createdDate.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += lastMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    if (days > 0) parts.push(`${days}d`);

    return parts.length > 0 ? parts.join(' ') : '0d';
  };

  const formatVisibility = (visibilityState) => {
    switch (visibilityState) {
      case 1: return 'Private';
      case 2: return 'Friends Only';
      case 3: return 'Public';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    if (urlSteamId) {
      setSteamId(urlSteamId);
      setInputValue(urlSteamId);
    }
  }, [urlSteamId]);

  useEffect(() => {
    if (!steamId) {
      setApiError(true);
      setSteamLoading(false);
      return;
    }

    let cancelled = false;
    // Hard timeout: the Steam section must never hang on a skeleton forever —
    // on abort we fall through to the error state (minimal steamid-input card).
    const controller = new AbortController();

    // Consume the head-start fetches from public/index.html (consume-once:
    // the global is deleted whether or not it matches, so it can never be
    // reused by a later mount, StrictMode's second pass, or another profile).
    let early = null;
    const stashed = window.__profileFetch;
    if (stashed) {
      delete window.__profileFetch;
      if (stashed.id === steamId && stashed.controller && stashed.summaries && stashed.bans) {
        early = stashed;
        // Chain the early fetches' controller to this effect's controller so
        // the existing 8s timeout AND the unmount cleanup bound them exactly
        // like component-initiated fetches.
        controller.signal.addEventListener('abort', () => stashed.controller.abort());
      } else if (stashed.controller) {
        stashed.controller.abort(); // stale/mismatched id: cancel zombie requests
      }
    }

    const timeout = setTimeout(() => controller.abort(), 8000);

    const fetchPlayerData = async () => {
      setSteamLoading(true);
      try {
        const summariesUrl = `/api/steam/ISteamUser/GetPlayerSummaries/v2/?steamids=${steamId}`;
        const bansUrl = `/api/steam/ISteamUser/GetPlayerBans/v1/?steamids=${steamId}`;

        const [summariesResponse, bansResponse] = await (early
          ? Promise.all([early.summaries, early.bans])
          : Promise.all([
              fetch(summariesUrl, { signal: controller.signal }),
              fetch(bansUrl, { signal: controller.signal })
            ]));

        if (cancelled) return;

        if (!summariesResponse.ok || !bansResponse.ok) {
          setApiError(true);
          return;
        }

        const summariesData = await summariesResponse.json();
        const bansData = await bansResponse.json();

        if (cancelled) return;

        if (summariesData.response && summariesData.response.players && summariesData.response.players.length > 0) {
          setPlayerData(summariesData.response.players[0]);
          setApiError(false);
          const source = new URLSearchParams(window.location.search).get('ref') || 'direct';
          analytics.trackProfileView(steamId, { source });
        } else {
          setApiError(true);
        }

        if (bansData.players && bansData.players.length > 0) {
          setBanData(bansData.players[0]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch player data:', error);
          setApiError(true);
        }
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setSteamLoading(false);
      }
    };

    fetchPlayerData();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [steamId]);

  // Steam level is a nice-to-have row: own request, outside the head-start
  // fetches in public/index.html, and no row at all if it fails.
  useEffect(() => {
    setSteamLevel(null);
    if (!/^\d{17}$/.test(steamId || '')) return;
    let cancelled = false;
    fetch(`/api/steam/IPlayerService/GetSteamLevel/v1/?steamid=${steamId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const level = json && json.response && json.response.player_level;
        if (!cancelled && typeof level === 'number') setSteamLevel(level);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [steamId]);

  // Fetch Leetify stats separately and progressively. This runs in parallel with
  // the Steam fetch above and is never awaited by the initial render, so it can't
  // delay first paint / LCP. The section shows a skeleton while loading and a
  // one-line placeholder on any unavailable/error/timeout outcome.
  useEffect(() => {
    if (!steamId || !/^\d{17}$/.test(steamId)) {
      setLeetify({ status: 'unavailable', data: null });
      return;
    }

    let cancelled = false;
    setLeetify({ status: 'loading', data: null });

    // Consume the head-start Leetify fetch from public/index.html (consume-once:
    // the global is deleted whether or not the id matches, so a later mount,
    // StrictMode's second pass, or another profile can never reuse it). On SPA
    // client-side navigation no full page load happened, so no global exists —
    // fall back to fetching normally. Either branch yields a json-or-null promise.
    let early = null;
    const stashed = window.__leetifyFetch;
    if (stashed) {
      delete window.__leetifyFetch;
      if (stashed.id === steamId && stashed.data) early = stashed.data;
    }

    (early || fetch(`/api/leetify/profile?steam64_id=${steamId}`).then((res) => (res.ok ? res.json() : null)))
      .then((data) => {
        if (cancelled) return;
        if (data && data.available) {
          setLeetify({ status: 'ready', data });
        } else {
          setLeetify({ status: 'unavailable', data: null, reason: data && data.reason });
        }
      })
      .catch(() => {
        if (!cancelled) setLeetify({ status: 'unavailable', data: null });
      });

    return () => {
      cancelled = true;
    };
  }, [steamId]);

  const handleInputChange = async (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (!value.trim()) {
      return;
    }

    const extracted = await extractSteamId(value);
    if (extracted) {
      setSteamId(extracted);
      setInputValue(extracted);
      navigate(`/profile/${extracted}`);
    }
  };

  const openAllQuickAccess = (via) => {
    analytics.trackToolInteraction('quick-access', 'open_all', { count: favorites.length, via });

    const urls = favorites
      .map(id => ALL_TOOLS[id])
      .filter(tool => tool)
      .map(tool => tool.urlTemplate(steamId));

    urls.reverse().forEach((url, index) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, index * 150);
    });
  };

  // 1-4 open a pinned tool, A opens all. Bare keys only, and never while typing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.repeat) return;
      const el = document.activeElement;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;

      if (e.key === 'a' || e.key === 'A') {
        if (!favorites.length) return;
        e.preventDefault();
        openAllQuickAccess('keyboard');
        return;
      }
      const tool = /^[1-4]$/.test(e.key) && ALL_TOOLS[favorites[Number(e.key) - 1]];
      if (!tool) return;
      e.preventDefault();
      analytics.trackToolClick(tool, { surface: 'quick-access', via: 'keyboard' });
      openTool(tool.urlTemplate(steamId));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // openAllQuickAccess only reads favorites + steamId, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, steamId]);


  return (
    <>
      <button
        className="sidebar-toggle"
        onClick={() => { setSidebarOpen(true); analytics.trackSidebar('open'); }}
        aria-label="Open navigation"
        aria-expanded={sidebarOpen}
      >
        [ ≡ ]
      </button>
      <Sidebar
        sections={sidebarSections}
        activeId={activeSection}
        onItemClick={scrollToSection}
        isOpen={sidebarOpen}
        onClose={() => { setSidebarOpen(false); analytics.trackSidebar('close'); }}
      />
      <div className="container pp-page">
      <header className="pp-header">
        <div>
          <h1 className="pp-title">// player.properties</h1>
          <div className="pp-subtitle"># CS2 Profile & Steam Statistics Hub</div>
        </div>
        <div className="pp-lookup">
          <label htmlFor="pp-lookup-input" className="pp-lookup-label">
            <span aria-hidden="true">lookup(</span>
            <span className="sr-only">Steam ID or profile URL</span>
          </label>
          <input
            id="pp-lookup-input"
            type="text"
            className="pp-lookup-input"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="steamid | profile url"
            spellCheck="false"
            autoComplete="off"
          />
          <span className="pp-lookup-label" aria-hidden="true">)</span>
        </div>
      </header>

      <div className="pp-layout">
        {steamLoading ? (
          <ProfileSkeleton />
        ) : playerData && !apiError ? (
          <ProfileCard
            steamId={steamId}
            player={playerData}
            bans={banData}
            steamLevel={steamLevel}
            leetify={leetify}
            inventory={inventory}
            accountAge={formatAccountAge(playerData.timecreated)}
            visibility={formatVisibility(playerData.communityvisibilitystate)}
          />
        ) : (
          <aside className="pp-card pp-profile">
            <div className="pp-comment">{'// profile not found'}</div>
            <p className="pp-muted">
              Steam returned nothing for this id. Paste a Steam64 ID, profile URL or
              vanity name into lookup( ).
            </p>
          </aside>
        )}

        <main className="pp-main">
          {leetify.status === 'loading' && <LeetifySkeleton />}
          {leetify.status === 'ready' && (
            <>
              <LeetifyStatCards data={leetify.data} />
              <LeetifyCard data={leetify.data} />
            </>
          )}
          {leetify.status === 'unavailable' && playerData && (
            <LeetifyPlaceholder steamId={steamId} reason={leetify.reason} />
          )}
          {csrep.status === 'loading' && (
            <div className="pp-card skeleton-block pp-skeleton-csrep" aria-hidden="true"></div>
          )}
          {csrep.status === 'ready' && <CsrepCard steamId={steamId} data={csrep.data} premierColor={premierTierColor} />}
          {csrep.status === 'unavailable' && playerData && (
            <CsrepPlaceholder steamId={steamId} reason={csrep.reason} />
          )}
          {inventory.status === 'loading' && (
            <div className="pp-card skeleton-block pp-skeleton-inventory" aria-hidden="true"></div>
          )}
          {(inventory.status === 'public' || inventory.status === 'unpriced') && (
            <InventoryCard key={steamId} data={inventory.data} steamId={steamId} />
          )}
        </main>
      </div>

      <button
        type="button"
        className="scroll-cue"
        onClick={() => scrollToSection('most-used-tools', 'scroll-cue')}
        aria-label="Scroll to most-used tools"
      >
        <span className="scroll-cue-line"></span>
        <span className="scroll-cue-text">
          {`// ${TOTAL_TOOL_COUNT} additional tools below`}{' '}
          <span className="scroll-cue-arrow">↓</span>
        </span>
        <span className="scroll-cue-line"></span>
      </button>

      <section id="quick-access" className="tools-section">
        <div className="tools-head">
          <div className="tools-title">
            <h2 className="tools-heading">{'// quick-access'}</h2>
            <span className="tools-sub">
              opens with {playerData?.personaname ? `${playerData.personaname}'s` : "this player's"} steamid
            </span>
          </div>
          <button
            type="button"
            className="qa-open-all"
            onClick={() => openAllQuickAccess('button')}
            disabled={!favorites.length}
            aria-keyshortcuts="A"
          >
            open all {favorites.length} ↗<kbd className="qa-open-all-key">A</kbd>
          </button>
        </div>
        <div className="qa-grid">
          {favorites.map((toolId, i) => {
            const tool = ALL_TOOLS[toolId];
            if (!tool) return null;
            return (
              <div key={toolId} className="qa-wrap">
                <a
                  href={tool.urlTemplate(steamId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qa-card"
                  data-tool-id={tool.id}
                  aria-keyshortcuts={String(i + 1)}
                  onClick={() => analytics.trackToolClick(tool, { surface: 'quick-access' })}
                >
                  <ToolTile tool={tool} steamId={steamId} size={48} />
                  <span className="qa-text">
                    <span className="tools-name">
                      <span className="tools-name-text">{tool.name}</span>
                      <span className="tools-arr" aria-hidden="true">↗</span>
                    </span>
                    <span className="qa-desc">{tool.desc}</span>
                  </span>
                  <kbd className="tools-key">{i + 1}</kbd>
                </a>
                <button
                  type="button"
                  className="qa-unpin"
                  onClick={() => togglePin(toolId, { surface: 'quick-access' })}
                  aria-label={`Unpin ${tool.name} from quick-access`}
                  title="Unpin"
                >
                  ×
                </button>
              </div>
            );
          })}
          {favorites.length < MAX_PINS && (
            <button
              type="button"
              className="qa-slot"
              onClick={() => scrollToSection('most-used-tools', 'pin-slot')}
            >
              + pin a tool below
            </button>
          )}
        </div>
      </section>

      <section id="most-used-tools" className="tools-section">
        <div className="tools-head">
          <div className="tools-title">
            <h2 className="tools-heading">{'// most-used-tools'}</h2>
            <span className="tools-sub">share of clicks across all users</span>
            <span className="tools-hint" role="status">
              {fullHintFor('most-used-tools')}
            </span>
          </div>
          <a
            href={`#${TOOLS_BY_CATEGORY[0].slug}`}
            className="pp-link tools-all"
            onClick={(e) => { e.preventDefault(); scrollToSection(TOOLS_BY_CATEGORY[0].slug, 'all-tools'); }}
          >
            [all tools →]
          </a>
        </div>
        <div className="mu-grid">
          {[mostUsedTools.slice(0, 5), mostUsedTools.slice(5)].map((col, c) => col.length > 0 && (
            <ol key={c} className="mu-col" start={c * 5 + 1}>
              {col.map(({ tool, rank, share, bar }) => {
                const pinned = favorites.includes(tool.id);
                const label = pinned ? 'Unpin from quick-access' : 'Pin to quick-access';
                return (
                  <li key={tool.id} className={`mu-row${rank <= 3 ? ' mu-top' : ''}`}>
                    <span className="mu-rank">#{rank}</span>
                    <a
                      href={tool.urlTemplate(steamId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mu-link"
                      data-tool-id={tool.id}
                      onClick={() => analytics.trackToolClick(tool, { surface: 'most-used-tools', category: tool.category })}
                    >
                      <ToolTile tool={tool} steamId={steamId} size={36} />
                      <span className="mu-text">
                        <span className="tools-name">
                          <span className="tools-name-text">{tool.name}</span>
                          <span className="tools-arr" aria-hidden="true">↗</span>
                        </span>
                        <span className="mu-desc">{tool.desc}</span>
                      </span>
                    </a>
                    <span className="mu-share">
                      <span className="mu-track"><span className="mu-fill" style={{ width: `${bar}%` }}></span></span>
                      <span className="mu-pct">{share == null ? '—' : `${Math.round(share)}%`}</span>
                    </span>
                    <button
                      type="button"
                      className={`mu-pin${pinned ? ' on' : ''}`}
                      onClick={() => pinFrom('most-used-tools', tool)}
                      aria-label={label}
                      aria-pressed={pinned}
                      title={label}
                    >
                      <PinIcon />
                    </button>
                  </li>
                );
              })}
            </ol>
          ))}
        </div>
      </section>

      <div className="dir">
        {TOOLS_BY_CATEGORY.map((cat) => {
          const grouped = cat.rows.reduce((n, r) => n + (r.members ? r.members.length : 0), 0);
          const hint = fullHintFor(cat.slug);
          return (
            <section key={cat.id} id={cat.slug} className="dir-cat" style={{ '--cat-color': cat.hoverColor }}>
              <div className="dir-head">
                <h2 className="dir-title">
                  <span className="dir-dot" aria-hidden="true"></span>
                  {`// ${cat.slug}`}
                </h2>
                <span className={`dir-note${hint ? ' tools-hint' : ''}`} role="status">
                  {hint || `${cat.tools.length} tool${cat.tools.length === 1 ? '' : 's'}${grouped ? `, ${grouped} near-identical grouped` : ''}`}
                </span>
              </div>
              <ul className="dir-rows">
                {cat.rows.map(({ tool, name, members }) => {
                  const pinned = favorites.includes(tool.id);
                  const ctx = { surface: cat.slug, category: tool.category };
                  return (
                    <li key={tool.id} className="dir-row">
                      <ToolTile tool={tool} steamId={steamId} size={32} />
                      {/* Stretched link: its ::after covers the row, so the whole row
                          opens the tool; pin + mirror chips sit above it. */}
                      <a
                        href={tool.urlTemplate(steamId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dir-name"
                        data-tool-id={tool.id}
                        onClick={() => analytics.trackToolClick(tool, ctx)}
                      >
                        {name}
                        <span className="tools-arr" aria-hidden="true"> ↗</span>
                      </a>
                      <span className="dir-desc">
                        {tool.desc}
                        {members && (
                          <span className="dir-mirrors">
                            {members.map((m) => (
                              <a
                                key={m.id}
                                href={m.urlTemplate(steamId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="dir-mirror"
                                data-tool-id={m.id}
                                onClick={() => analytics.trackToolClick(m, ctx)}
                              >
                                {hostOf(m.urlTemplate(steamId))}
                              </a>
                            ))}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        className={`dir-pin${pinned ? ' on' : ''}`}
                        onClick={() => pinFrom(cat.slug, tool)}
                        aria-pressed={pinned}
                        aria-label={pinned ? `Unpin ${name}` : `Pin ${name} to quick-access`}
                      >
                        {pinned ? 'pinned' : 'pin'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <div className="footer">
        <a href="/faq" className="faq-link">FAQ</a> • <a href="/privacy" className="faq-link">Privacy</a> • <a href="/feedback" className="faq-link">Feedback</a> • <a href="https://ko-fi.com/oddlobster" className="faq-link" target="_blank" rel="noopener noreferrer">Support me with ☕</a> • Built by a CS player who got tired of remembering multiple different TLD shortcuts
      </div>
      </div>
    </>
  );
}

export default App;
